import React, { useEffect, useState } from "react";
import { FunctionComponent } from "react";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import {
  closeFilters,
  fetchCars,
  openFilters,
  selectFilterableValues,
  selectFilters,
  selectFiltersOpened,
  updateFilters,
} from "../slices/cars-slice";
import {
  Autocomplete,
  Box,
  Button,
  Drawer,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  EmojiEvents,
  FilterAlt,
  FilterAltOff,
  Replay,
} from "@mui/icons-material";
import { FILTER_KEYS } from "../models/filter";

export const CarFilters: FunctionComponent = () => {
  const filterableValues = useAppSelector(selectFilterableValues);
  const filters = useAppSelector(selectFilters);
  const filtersOpened = useAppSelector(selectFiltersOpened);
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const nbActiveFilters = Object.values(filters).filter(
    (value) => value != undefined,
  ).length;

  const handleSelectFilter = (key: string, value: string) => {
    const updatedFilters = { ...filters, [key]: value || undefined };
    dispatch(updateFilters(updatedFilters));
    dispatch(fetchCars());
  };

  const handleResetFilters = () => {
    dispatch(updateFilters({}));
    dispatch(fetchCars());
  };

  const handleOpenFilters = () => {
    dispatch(openFilters());
  };

  const handleCloseFilters = () => {
    dispatch(closeFilters());
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);

    FILTER_KEYS.forEach((key) => {
      const value = filters[key];

      if (value === undefined || value === null || value === "") {
        searchParams.delete(key);
      } else {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            searchParams.append(key, String(item));
          });
        } else {
          searchParams.set(key, String(value));
        }
      }
    });

    const queryString = searchParams.toString();

    const newUrl = queryString
      ? `${window.location.pathname}?${queryString}`
      : window.location.pathname;
    window.history.replaceState(null, "", newUrl);
  }, [filters]);

  const filterComponents = (
    <>
      <Autocomplete
        sx={{ width: { xs: "100%", sm: 150 } }}
        noOptionsText="Aucune option"
        options={filterableValues["status"]}
        value={filters.status || null}
        onChange={(_event, newValue) => handleSelectFilter("status", newValue)}
        renderInput={(params) => <TextField {...params} label="Statut" />}
      />

      <Autocomplete
        sx={{ width: { xs: "100%", sm: 200 } }}
        noOptionsText="Aucune option"
        options={filterableValues["brand"]}
        value={filters.brand || null}
        onChange={(_event, newValue) => handleSelectFilter("brand", newValue)}
        renderInput={(params) => <TextField {...params} label="Constructeur" />}
      />

      <Autocomplete
        sx={{ width: { xs: "100%", sm: 120 } }}
        noOptionsText="Aucune option"
        options={filterableValues["year"]}
        value={filters.year || null}
        onChange={(_event, newValue) => handleSelectFilter("year", newValue)}
        renderInput={(params) => <TextField {...params} label="Année" />}
        getOptionLabel={(option) => option.toString()}
      />

      <Autocomplete
        sx={{ width: { xs: "100%", sm: 220 } }}
        noOptionsText="Aucune option"
        options={filterableValues["manufacturer"]}
        value={filters.manufacturer || null}
        onChange={(_event, newValue) =>
          handleSelectFilter("manufacturer", newValue)
        }
        renderInput={(params) => <TextField {...params} label="Fabricant" />}
      />

      <Autocomplete
        sx={{ width: { xs: "100%", sm: 130 } }}
        noOptionsText="Aucune option"
        options={filterableValues["category"]}
        value={filters.category || null}
        onChange={(_event, newValue) =>
          handleSelectFilter("category", newValue)
        }
        renderInput={(params) => <TextField {...params} label="Catégorie" />}
      />

      <Autocomplete
        sx={{ width: { xs: "100%", sm: 110 } }}
        noOptionsText="Aucune option"
        options={["true", "false"]}
        value={filters.categoryWinner || null}
        getOptionLabel={(option) => {
          return option === "true" ? "Oui" : "Non";
        }}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          return (
            <Box key={key} component="li" {...optionProps}>
              {option === "true" ? "Oui" : "Non"}
            </Box>
          );
        }}
        onChange={(_event, newValue) =>
          handleSelectFilter("categoryWinner", newValue)
        }
        renderInput={(params) => <TextField {...params} label="Winner" />}
      />

      <Autocomplete
        sx={{ width: { xs: "100%", sm: 140 } }}
        noOptionsText="Aucune option"
        options={filterableValues["owner"]}
        value={filters.owner || null}
        onChange={(_event, newValue) => handleSelectFilter("owner", newValue)}
        renderInput={(params) => <TextField {...params} label="Propriétaire" />}
      />
    </>
  );

  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
      {isMobile ? (
        <>
          <Button
            startIcon={
              nbActiveFilters > 0 ? (
                <FilterAlt color="primary" />
              ) : (
                <FilterAltOff />
              )
            }
            onClick={handleOpenFilters}
          >
            Filtrer
          </Button>
          <Drawer
            anchor="right"
            open={filtersOpened}
            onClose={handleCloseFilters}
          >
            <Box sx={{ width: 250, p: 2 }}>
              <Stack spacing={2}>{filterComponents}</Stack>
              {nbActiveFilters > 0 && (
                <Button
                  startIcon={<Replay color="primary" />}
                  onClick={handleResetFilters}
                >
                  Réinitialiser
                </Button>
              )}
            </Box>
          </Drawer>
        </>
      ) : (
        <>
          {nbActiveFilters > 0 ? (
            <FilterAlt color="primary" />
          ) : (
            <FilterAltOff />
          )}

          {filterComponents}

          {nbActiveFilters > 0 && (
            <IconButton onClick={handleResetFilters}>
              <Replay color="primary" />
            </IconButton>
          )}
        </>
      )}
    </Stack>
  );
};
